

import { useState, useEffect } from "react";
import { VscCheckAll } from "react-icons/vsc";
import { RxCross2 } from "react-icons/rx";
import { IoChevronDown, IoChevronForward } from "react-icons/io5";
import Button from "../Button";
import Pagination from "../Pagination";
import { supabase, fetchInChunks } from "../../supabaseClient";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { AiOutlineClockCircle } from "react-icons/ai";
// at top of file (replace react-beautiful-dnd import if present)
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Swal from "sweetalert2";




export default function SelectPage({
  onBack,
  onSave,
  fetchItems,
  selectedPages = [],
  startTime,
  endTime,
  setStartTime,
  setEndTime,
  initialTTL = [],
}) {
  // === state ===
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [expandedPages, setExpandedPages] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [pageRowsMap, setPageRowsMap] = useState({});
  const [rowTilesMap, setRowTilesMap] = useState({});

  // === ISOLATED TIME STRUCTURE (per-page) ===
  // pageTimes[pageId] = { start: Date|null, end: Date|null }
  const [pageTimes, setPageTimes] = useState({});
  // rowTimes[pageId] = { [rowId]: { start, end } }
  const [rowTimes, setRowTimes] = useState({});
  // tileTimes[pageId] = { [rowId]: { [tileId]: { start, end } } }
  const [tileTimes, setTileTimes] = useState({});

  const [editingPages, setEditingPages] = useState({});
  const [editingRows, setEditingRows] = useState({});
  const [editingTiles, setEditingTiles] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const DATE_FMT = "MMM dd hh:mm aa";
  const pageSize = 16;

  // === helpers ===
  const norm = (d) => {
    if (!d && d !== 0) return null;
    if (d instanceof Date) return new Date(d.getTime());
    const parsed = new Date(d);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const getTime = (d) => (d ? norm(d).getTime() : null);

  const isBefore = (a, b) => {
    const ta = getTime(a);
    const tb = getTime(b);
    if (ta == null || tb == null) return false;
    return ta < tb;
  };
  const isAfter = (a, b) => {
    const ta = getTime(a);
    const tb = getTime(b);
    if (ta == null || tb == null) return false;
    return ta > tb;
  };

  // Force-correct date when user picks time-only
  const forceCorrectDate = (val, refDate) => {
    if (!val || !refDate) return norm(val) || norm(refDate);
    const corrected = new Date(refDate);
    corrected.setHours(val.getHours());
    corrected.setMinutes(val.getMinutes());
    corrected.setSeconds(val.getSeconds());
    corrected.setMilliseconds(val.getMilliseconds());
    return corrected;
  };


  useEffect(() => {
  if (!startTime) return;

  const min = norm(startTime);

  // --- clamp pages ---
  setPageTimes(prev => {
    const updated = { ...prev };
    for (const pid in updated) {
      if (updated[pid]?.start && updated[pid].start < min) {
        updated[pid].start = min;
      }
      if (updated[pid]?.end && updated[pid].end < updated[pid].start) {
        updated[pid].end = updated[pid].start;
      }
    }
    return updated;
  });

  // --- clamp rows ---
  setRowTimes(prev => clampNestedTimes(prev, min, norm(endTime)));

  // --- clamp tiles ---
  setTileTimes(prev => clampNestedTimes(prev, min, norm(endTime)));
}, [startTime]);


  const showTimeError = (message) => {
    Swal.fire({
      position: "top-end",
      icon: "warning",
      title: message,
      showConfirmButton: false,
      timer: 2500,
      timerProgressBar: true,
      toast: true,
      background: "white",
      customClass: {
        popup: "shadow-lg rounded-md p-3 text-sm",
      },
    });
  };

  // clamp nested times to global start/end range
  const clampNestedTimes = (nestedObj, min, max) => {
    const out = Array.isArray(nestedObj) ? [] : {};
    for (const k in nestedObj) {
      const val = nestedObj[k];
      if (val && typeof val === "object" && (val.start !== undefined || val.end !== undefined)) {
        let s = norm(val.start);
        let e = norm(val.end);
        if (s && min && s < min) s = min;
        if (e && max && e > max) e = max;
        if (s && e && e < s) e = s;
        out[k] = { start: s, end: e };
      } else if (val && typeof val === "object") {
        // deeper nesting (for rowTimes[pageId] or tileTimes[pageId][rowId])
        out[k] = clampNestedTimes(val, min, max);
      } else {
        out[k] = val;
      }
    }
    return out;
  };

  // isOverridden: check if child start/end differ from parent
  const isOverridden = (child, parent) => {
    if (!child) return false;
    const cs = getTime(child.start);
    const ce = getTime(child.end);
    const ps = getTime(parent.start);
    const pe = getTime(parent.end);

    if (cs != null && ps != null && cs !== ps) return true;
    if (ce != null && pe != null && ce !== pe) return true;
    // if child has start/end while parent lacks them, treat as override
    if ((cs != null || ce != null) && (ps == null && pe == null)) return true;
    return false;
  };

  // detect if any row/tile under a page has overrides (for page-child coloring)
  const hasPageChildOverride = (pageId) => {
    const pTime = pageTimes[pageId] || { start: norm(startTime), end: norm(endTime) };
    // check rowTimes[pageId]
    const rTimes = rowTimes[pageId] || {};
    for (const rid in rTimes) {
      if (isOverridden(rTimes[rid], pTime)) return true;
    }
    // check tileTimes[pageId]
    const tPage = tileTimes[pageId] || {};
    for (const rid in tPage) {
      const tilesOfRow = tPage[rid] || {};
      for (const tid in tilesOfRow) {
        if (isOverridden(tilesOfRow[tid], rTimes[rid] || pTime)) return true;
      }
    }
    return false;
  };

  const hasRowTileOverride = (pageId, row) => {
    const pTime = pageTimes[pageId] || { start: norm(startTime), end: norm(endTime) };
    const rTime = (rowTimes[pageId] || {})[row.id] || {};
    const tilesOfRow = (tileTimes[pageId] || {})[row.id] || {};
    for (const tid in tilesOfRow) {
      if (isOverridden(tilesOfRow[tid], rTime.start || rTime.end ? rTime : pTime)) return true;
    }
    return false;
  };

  // === TTL restore from initialTTL ===
  useEffect(() => {
    console.log("🟨 initialTTL RESTORE triggered", { initialTTL, itemsLen: items.length });
    if (!initialTTL || initialTTL.length === 0 || items.length === 0) {
      console.log("🟥 initialTTL RESTORE exit (no TTL or no items)");
      return;
    }

    const newPageTimes = {};
    const newRowTimes = {};
    const newTileTimes = {};

    initialTTL.forEach((page) => {
      const pid = page.pid;
      newPageTimes[pid] = {
        start: page.st ? new Date(page.st) : null,
        end: page.et ? new Date(page.et) : null,
      };
      (page.rows || []).forEach((row) => {
        if (!newRowTimes[pid]) newRowTimes[pid] = {};
        newRowTimes[pid][row.rid] = {
          start: row.st ? new Date(row.st) : null,
          end: row.et ? new Date(row.et) : null,
        };
        (row.tiles || []).forEach((tile) => {
          if (!newTileTimes[pid]) newTileTimes[pid] = {};
          if (!newTileTimes[pid][row.rid]) newTileTimes[pid][row.rid] = {};
          newTileTimes[pid][row.rid][tile.tid] = {
            start: tile.st ? new Date(tile.st) : null,
            end: tile.et ? new Date(tile.et) : null,
          };
        });
      });
    });

    setPageTimes(newPageTimes);
    setRowTimes(newRowTimes);
    setTileTimes(newTileTimes);
  }, [initialTTL, items]);

  // === fetch items ===
  useEffect(() => {
    console.log("🟦 fetchItems called", { page, searchTerm });
    const load = async () => {
      const { data, total } = await fetchItems(page, pageSize, searchTerm);
      console.log("🟪 fetchItems returned", { itemsCount: data.length });
      setItems(data);
      setTotalPages(total);
      console.log("🟧 selectedPages restore triggered", selectedPages);

      setSelected((prev) => {
        const already = prev.map((x) => x.id);
        const pageIds = selectedPages.map(String);
        const restored = data.filter((item) => pageIds.includes(String(item.id)));
        return [...prev, ...restored.filter((x) => !already.includes(x.id))];
      });
    };
    load();
  }, [page, searchTerm, selectedPages]);

  // === clamp global end change to nested times ===
  // useEffect(() => {
  //   // whenever global endTime changes, clamp nested times to [startTime, endTime]
  //   if (!startTime || !endTime) return;
  //   setPageTimes((prev) => clampNestedTimes(prev, norm(startTime), norm(endTime)));
  //   setRowTimes((prev) => clampNestedTimes(prev, norm(startTime), norm(endTime)));
  //   setTileTimes((prev) => clampNestedTimes(prev, norm(startTime), norm(endTime)));
  // }, [startTime, endTime]);

  // === selection toggles ===
  const toggleSelect = (item) =>
    setSelected((prev) =>
      prev.some((x) => x.id === item.id) ? prev.filter((x) => x.id !== item.id) : [...prev, item]
    );

  const toggleAll = () => {
    if (selected.length === items.length) setSelected([]);
    else setSelected(items);
  };

  const isFirstSave = () => {
  return (
    !initialTTL ||
    initialTTL.length === 0 ||
    initialTTL.every(p => !p.rows || p.rows.length === 0)
  );
};


  // === Save (build TTL using client order + isolated times) ===
  const handleSaveClick = async () => {
    if (selected.length === 0) {
      return showTimeError("Select at least 1 page");
    }
    
    setIsSaving(true);

    try {
      const firstSave = isFirstSave();

      // these will be used ONLY for saving TTL
      let effectivePageRowsMap = { ...pageRowsMap };
      let effectiveRowTilesMap = { ...rowTilesMap };

      if (firstSave) {
        console.log("🟢 FIRST SAVE → USING TV ORDER");

        for (const page of selected) {
          const pageId = page.id;

          // 1️⃣ get TV row order
          const { data: pageData } = await supabase
            .from("pages")
            .select("rows")
            .eq("id", pageId)
            .single();

          const rowIds = Array.isArray(pageData.rows)
            ? pageData.rows
            : JSON.parse(pageData.rows || "[]");

          if (!rowIds.length) {
            effectivePageRowsMap[pageId] = [];
            continue;
          }

          const { data: rowsData } = await supabase
            .from("row_data")
            .select("*")
            .in("id", rowIds);

          const orderedRows = rowIds
            .map(rid => rowsData.find(r => r.id === rid))
            .filter(Boolean);

          effectivePageRowsMap[pageId] = orderedRows;

          // 2️⃣ get TV tile order
          for (const row of orderedRows) {
            const tids = (row.row_items || []).map(t => t.tid);

            if (!tids.length) {
              effectiveRowTilesMap[pageId] = {
                ...(effectiveRowTilesMap[pageId] || {}),
                [row.id]: []
              };
              continue;
            }

            const { data: tilesData } = await fetchInChunks("tiles", "tid", "tid", tids);

            const orderedTiles = tids
              .map(tid => tilesData.find(t => t.tid === tid))
              .filter(Boolean);

            effectiveRowTilesMap[pageId] = {
              ...(effectiveRowTilesMap[pageId] || {}),
              [row.id]: orderedTiles
            };
          }
        }
      }


      // load rows + tiles for each selected page to ensure we include actual rows
      const newPageRowsMap = {};
      const newRowTilesMap = {};

      for (const page of selected) {
        const pageId = page.id;

        const { data: pageData } = await supabase.from("pages").select("rows").eq("id", pageId).single();

        const rowIds = Array.isArray(pageData.rows) ? pageData.rows : JSON.parse(pageData.rows || "[]");
        const { data: rowsData } = await supabase.from("row_data").select("*").in("id", rowIds);
        newPageRowsMap[pageId] = rowsData || [];

        for (const row of rowsData) {
          const tids = (row.row_items || []).map((r) => r.tid);
          if (tids.length > 0) {
            const { data: tilesData } = await fetchInChunks("tiles", "tid, title, poster, square", "tid", tids);
            if (rowTilesMap[pageId]?.[row.id]) {
              newRowTilesMap[row.id] = rowTilesMap[pageId][row.id];
            } else {
              newRowTilesMap[row.id] = tilesData || [];
            }
          } else {
            newRowTilesMap[row.id] = [];
          }
        }
      }

      // Build TTL from client state (pageRowsMap + rowTilesMap)
      const TTL = selected.map((pageItem) => {
        const pageId = pageItem.id;
        const pageStart = pageTimes[pageId]?.start || norm(startTime);
        const pageEnd = pageTimes[pageId]?.end || norm(endTime);
        // const rows = pageRowsMap[pageId] || [];
        const rows = effectivePageRowsMap[pageId] || [];


        return {
          pid: pageId,
          st: pageStart,
          et: pageEnd,
          rows: rows.map((row) => {
            const rowStart = (rowTimes[pageId] && rowTimes[pageId][row.id]?.start) || pageStart;
            const rowEnd = (rowTimes[pageId] && rowTimes[pageId][row.id]?.end) || pageEnd;

            // const tiles = rowTilesMap[pageId]?.[row.id] || [];
            const tiles = effectiveRowTilesMap[pageId]?.[row.id] || [];


            return {
              rid: row.id,
              st: rowTimes[pageId]?.[row.id]?.start || null,
              et: rowTimes[pageId]?.[row.id]?.end || null,
              tiles: tiles.map((tile) => ({
                tid: tile.tid,
                st: tileTimes[pageId]?.[row.id]?.[tile.tid]?.start || null,
                et: tileTimes[pageId]?.[row.id]?.[tile.tid]?.end || null,
              })),
            };
          }),
        };
      });
      console.log("💾 FINAL TTL SAVE", JSON.parse(JSON.stringify(TTL)));


      await onSave(selected, TTL);
    } catch (error) {
      console.error("Save error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // === Expand / load rows (apply TTL order if present) ===
  const togglePageExpand = async (pageItem) => {
    const pageId = pageItem.id;
    setExpandedPages((prev) => ({ ...prev, [pageId]: !prev[pageId] }));

    // if already loaded and no TTL override, skip
    const ttlPage = initialTTL.find((p) => p.pid === pageId);
    // if (pageRowsMap[pageId] && !ttlPage) {
    //   return;
    // }

    // fetch raw rows
    const { data } = await supabase.from("pages").select("rows").eq("id", pageId).single();
    if (!data?.rows) return;

    const rowIds = Array.isArray(data.rows) ? data.rows : JSON.parse(data.rows || "[]");
    if (rowIds.length === 0) {
      setPageRowsMap((prev) => ({ ...prev, [pageId]: [] }));
      return;
    }

    const { data: rowsData } = await supabase.from("row_data").select("*").in("id", rowIds);
    let finalRows = rowsData || [];

    // Apply TTL row order if present
    if (ttlPage && ttlPage.rows) {
      const ttlOrder = ttlPage.rows.map((r) => r.rid);
      const ordered = ttlOrder.map((rid) => finalRows.find((r) => r.id === rid)).filter(Boolean);
      const leftovers = finalRows.filter((row) => !ttlOrder.includes(row.id));
      finalRows = [...ordered, ...leftovers];
    }

    setPageRowsMap((prev) => ({ ...prev, [pageId]: finalRows }));
  };

  // === Expand row -> load tiles (store under pageId bucket) ===
  const toggleRowExpand = async (pageId, row) => {
    const rowId = row.id;
    setExpandedRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));

    // if (rowTilesMap[pageId]?.[rowId]) return;

    const tids = (row.row_items || []).map((r) => r.tid);
    if (tids.length === 0) return;

    const { data: tilesData } = await fetchInChunks("tiles", "tid, title, poster, square", "tid", tids);
    let finalTiles = tilesData || [];

    // apply TTL ordering for tiles if present
    const ttlPage = initialTTL.find((p) => p.pid === pageId);
    if (ttlPage) {
      const ttlRow = ttlPage.rows?.find((r) => r.rid === rowId);
      if (ttlRow?.tiles) {
        const ttlOrder = ttlRow.tiles.map((t) => t.tid);
        finalTiles = ttlOrder.map((tid) => tilesData.find((t) => t.tid === tid)).filter(Boolean);
        const leftovers = tilesData.filter((t) => !ttlOrder.includes(t.tid));
        finalTiles = [...finalTiles, ...leftovers];
      }
    }

    setRowTilesMap((prev) => ({
      ...prev,
      [pageId]: { ...(prev[pageId] || {}), [rowId]: finalTiles },
    }));
  };

  // === Reorder handlers (respect page buckets) ===
  // const handleTileReorder = (pageId, rowId, sourceIndex, destIndex) => {
  //   setRowTilesMap((prev) => {
  //     const pageData = prev[pageId] || {};
  //     const current = [...(pageData[rowId] || [])];
  //     const [moved] = current.splice(sourceIndex, 1);
  //     current.splice(destIndex, 0, moved);

  //     return {
  //       ...prev,
  //       [pageId]: {
  //         ...pageData,
  //         [rowId]: current,
  //       },
  //     };
  //   });
  // };
  const handleTileReorder = (pageId, rowId, sourceIndex, destIndex) => {
  console.log("🟠 TILE REORDER", {
    pageId,
    rowId,
    from: sourceIndex,
    to: destIndex,
    before: rowTilesMap[pageId]?.[rowId]?.map(t => t.tid),
  });

  setRowTilesMap((prev) => {
    const pageData = prev[pageId] || {};
    const current = [...(pageData[rowId] || [])];
    const [moved] = current.splice(sourceIndex, 1);
    current.splice(destIndex, 0, moved);

    console.log("🟪 TILE REORDER RESULT", {
      pageId,
      rowId,
      after: current.map(t => t.tid),
    });

    return {
      ...prev,
      [pageId]: {
        ...pageData,
        [rowId]: current,
      },
    };
  });
};



  const handleRowReorder = (pageId, sourceIndex, destIndex) => {
  console.log("🔵 ROW REORDER", {
    pageId,
    from: sourceIndex,
    to: destIndex,
    before: pageRowsMap[pageId]?.map(r => r.id),
  });

  setPageRowsMap((prev) => {
    const current = [...(prev[pageId] || [])];
    const [moved] = current.splice(sourceIndex, 1);
    current.splice(destIndex, 0, moved);

    console.log("🟣 ROW REORDER RESULT", {
      pageId,
      after: current.map(r => r.id),
    });

    return {
      ...prev,
      [pageId]: current,
    };
  });
};



  const onDragEnd = (result) => {
    const { source, destination, type } = result;
    if (!destination) return;

    if (type === "ROW") {
      const pageId = source.droppableId.split("rows-")[1];
      handleRowReorder(pageId, source.index, destination.index);
      return;
    }

    if (type !== "ROW") {
      const [pageId, rowId] = source.droppableId.split("__");
      const [dPageId, dRowId] = destination.droppableId.split("__");
      if (pageId === dPageId && rowId === dRowId) {
        handleTileReorder(pageId, rowId, source.index, destination.index);
      }
    }
  };


  useEffect(() => {
  if (!items.length || !initialTTL.length) {
    console.log("❌ Restore skipped — no items or no TTL");
    return;
  }

  console.log("⚡ FULL RESTORE STARTING...");

  const newPageTimes = {};
  const newRowTimes = {};
  const newTileTimes = {};
  const newPageRowsMap = {};
  const newRowTilesMap = {};
  const restoredSelected = [];

  (async () => {
    for (const page of initialTTL) {
      const pid = page.pid;
      restoredSelected.push(pid);

      console.log("➡ Restoring page:", pid);

      // times restore
      newPageTimes[pid] = {
        start: page.st ? new Date(page.st) : null,
        end: page.et ? new Date(page.et) : null,
      };

      // ----- load rows -----
      const { data: pageData } =
        await supabase.from("pages").select("rows").eq("id", pid).single();

      const rowIds = Array.isArray(pageData.rows)
        ? pageData.rows
        : JSON.parse(pageData.rows || "[]");

      const { data: rowsData } =
        await supabase.from("row_data").select("*").in("id", rowIds);

      // ordering
      const orderedRows = page.rows
        .map((r) => rowsData.find((x) => x.id === r.rid))
        .filter(Boolean);

      const leftovers = rowsData.filter(
        (r) => !page.rows.map((x) => x.rid).includes(r.id)
      );

      newPageRowsMap[pid] = [...orderedRows, ...leftovers];

      // ----- per-row times + load tiles -----
      for (const row of page.rows) {
        const rid = row.rid;

        newRowTimes[pid] = newRowTimes[pid] || {};
        newRowTimes[pid][rid] = {
          start: row.st ? new Date(row.st) : null,
          end: row.et ? new Date(row.et) : null,
        };

        // load tiles
        const tids = row.tiles.map((t) => t.tid);

        const { data: tilesData } = await fetchInChunks("tiles", "*", "tid", tids);

        const orderedTiles = row.tiles
          .map((t) => tilesData.find((x) => x.tid === t.tid))
          .filter(Boolean);

        const tileLeftovers = tilesData.filter(
          (t) => !row.tiles.map((x) => x.tid).includes(t.tid)
        );

        newRowTilesMap[pid] = newRowTilesMap[pid] || {};
        newRowTilesMap[pid][rid] = [...orderedTiles, ...tileLeftovers];

        // tile times
        newTileTimes[pid] = newTileTimes[pid] || {};
        newTileTimes[pid][rid] = {};

        row.tiles.forEach((tile) => {
          newTileTimes[pid][rid][tile.tid] = {
            start: tile.st ? new Date(tile.st) : null,
            end: tile.et ? new Date(tile.et) : null,
          };
        });
      }
    }

    console.log("🔥 APPLYING RESTORE INTO STATE");

    setSelected(items.filter((x) => restoredSelected.includes(x.id)));
    setPageTimes(newPageTimes);
    setRowTimes(newRowTimes);
    setTileTimes(newTileTimes);
    setPageRowsMap(newPageRowsMap);
    setRowTilesMap(newRowTilesMap);

    // expand all restored pages
    // const expanded = {};
    // restoredSelected.forEach((pid) => (expanded[pid] = true));
    // setExpandedPages(expanded);
    // do NOT auto-expand pages on restore
setExpandedPages({});


    console.log("✅ FULL RESTORE COMPLETE");
  })();
}, [items, initialTTL]);


  // === UI render helpers to read isolated times ===
  const getPageStart = (pageId) => pageTimes[pageId]?.start || norm(startTime);
  const getPageEnd = (pageId) => pageTimes[pageId]?.end || norm(endTime);
  const getRowStart = (pageId, rowId) => rowTimes[pageId]?.[rowId]?.start || null;
  const getRowEnd = (pageId, rowId) => rowTimes[pageId]?.[rowId]?.end || null;
  const getTileStart = (pageId, rowId, tileId) => tileTimes[pageId]?.[rowId]?.[tileId]?.start || null;
  const getTileEnd = (pageId, rowId, tileId) => tileTimes[pageId]?.[rowId]?.[tileId]?.end || null;

  // === Render ===
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="h-screen  bg-gray-50">
        <div className="border border-gray-300 rounded-md p-4 h-[calc(100vh-7rem)] overflow-auto space-y-4">
          {/* Header Controls */}
          <div className="flex justify-between items-center flex-wrap gap-3">
            {/* Left Side */}
            <div className="flex items-center gap-3">
              <Button
                variant={selected.length === items.length ? "danger" : "success"}
                onClick={toggleAll}
                className="px-4 py-1 text-sm flex items-center gap-3"
              >
                <VscCheckAll />
                {selected.length === items.length ? "Uncheck All" : "Check All"}
              </Button>

              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                placeholder="Search..."
                className="px-3 py-2 border rounded-md text-sm w-60"
              />
            </div>

            {/* Right Side → Global Time Controls */}
            <div className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-2 rounded-md border border-gray-200 flex-wrap justify-end">
              <span className="font-medium text-gray-700">Start:</span>
              <DatePicker
                selected={norm(startTime)}
                onChange={(val) => {
                  const v = norm(val);
                  if (v && norm(endTime) && isAfter(v, norm(endTime))) return showTimeError("Start must be before End");
                  setStartTime(v);
                }}
                showTimeSelect
                timeFormat="hh:mm aa"
                dateFormat={DATE_FMT}
                timeIntervals={15}
                className="border border-gray-300 rounded-md px-2 py-[3px] text-xs w-[110px]"
              />
              <span className="font-medium text-gray-700">End:</span>
              <DatePicker
                selected={norm(endTime)}
                onChange={(val) => {
                  const v = norm(val);
                  if (v && norm(startTime) && isBefore(v, norm(startTime))) return showTimeError("End must be after Start");

                  // clamp all restored nested values
                  setPageTimes((prev) => clampNestedTimes(prev, norm(startTime), v));
                  setRowTimes((prev) => clampNestedTimes(prev, norm(startTime), v));
                  setTileTimes((prev) => clampNestedTimes(prev, norm(startTime), v));

                  setEndTime(v);
                }}
                showTimeSelect
                timeFormat="hh:mm aa"
                dateFormat={DATE_FMT}
                timeIntervals={15}
                className="border border-gray-300 rounded-md px-2 py-[3px] text-xs w-[110px]"
              />
            </div>
          </div>

          {/* Selected Preview */}
          {selected.length > 0 && (
            <div className="border border-gray-300 rounded-md p-3 bg-gray-50">
              <h3 className="text-sm font-semibold mb-2">Selected ({selected.length})</h3>
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                {selected.map((item) => (
                  <div key={item.id} className="flex items-center gap-2 bg-gray-200 rounded px-2 py-1 text-sm">
                    {item.page_name}
                    <button
                      type="button"
                      onClick={() => setSelected((prev) => prev.filter((x) => x.id !== item.id))}
                      className="text-red-600 hover:text-red-800"
                    >
                      <RxCross2 />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pages + Rows + Tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((pageItem) => {
              const pageId = pageItem.id;
              const isSelected = selected.some((x) => x.id === pageItem.id);
              const isExpanded = expandedPages[pageId];

              const pageOverridden = isOverridden(pageTimes[pageId], { start: norm(startTime), end: norm(endTime) });
              const childOverridden = hasPageChildOverride(pageId);

              return (
                <div
                  key={pageItem.id}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 shadow-sm
                  ${
                    pageOverridden
                      ? "border-yellow-500 bg-yellow-50"
                      : childOverridden
                      ? "border-blue-500 bg-blue-50"
                      : isSelected
                      ? "border-green-600 bg-green-50/40"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <div className="flex flex-col gap-1">
                    {/* Top bar with name + expand */}
                    <div className="flex items-center justify-between font-medium">
                      {/* Left: Name + Clock */}
                      <div className="flex items-center">
                        <span onClick={() => toggleSelect(pageItem)} className="cursor-pointer">
                          {pageItem.page_name}
                        </span>

                        {/* Clock with 6px gap */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingPages((prev) => ({ ...prev, [pageId]: !prev[pageId] }));
                          }}
                          className="ml-[10px] text-blue-600 hover:text-blue-800"
                        >
                          <AiOutlineClockCircle size={14} />
                        </button>
                      </div>

                      {/* Right: Expand Arrow */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePageExpand(pageItem);
                        }}
                        className="text-gray-600 bg-green-200 rounded-full hover:text-black mr-[2px]"
                      >
                        {isExpanded ? <IoChevronDown /> : <IoChevronForward />}
                      </button>
                    </div>

                    {/* Show pickers only if editing (page) */}
                    {editingPages[pageId] && (
                      <div className="flex items-center gap-1 text-xs mt-1">
                        <DatePicker
                          selected={pageTimes[pageId]?.start || norm(startTime)}
                          onChange={(rawVal) => {
                            // validation/clamping similar to reference
                            const pageStartOld = pageTimes[pageId]?.start || norm(startTime);
                            const pageEndOld = pageTimes[pageId]?.end || norm(endTime);

                            // If user selected date-only (midnight) — treat as date-only and set directly
                            const isDateOnly =
                              rawVal.getHours() === 0 && rawVal.getMinutes() === 0 && rawVal.getSeconds() === 0;
                            if (isDateOnly) {
                              const v = new Date(rawVal);
                              if (v < norm(startTime)) return showTimeError("Page start cannot be earlier than scheduling start");
                              if ((pageEndOld && v > pageEndOld)) return showTimeError("Page start cannot exceed its end time");
                              console.log("🟩 PAGE START TIME CHANGE", {
  pageId,
  newStart: v,
  oldStart: pageStartOld,
  oldEnd: pageEndOld,
});

                              setPageTimes((prev) => ({ ...prev, [pageId]: { ...(prev[pageId] || {}), start: v } }));
                              return;
                            }

                            // time-only → keep date from previous
                            const rawDate = rawVal.toDateString();
                            const oldDate = (pageStartOld || new Date()).toDateString();

                            let val;
                            if (rawDate !== oldDate) {
                              val = new Date(rawVal);
                            } else {
                              val = new Date(pageStartOld || norm(startTime));
                              val.setHours(rawVal.getHours());
                              val.setMinutes(rawVal.getMinutes());
                              val.setSeconds(rawVal.getSeconds());
                            }

                            if (val < norm(startTime)) return showTimeError("Page start cannot be earlier than scheduling start");
                            if ((pageEndOld && val > pageEndOld)) return showTimeError("Page start cannot exceed its end time");

                            setPageTimes((prev) => ({ ...prev, [pageId]: { ...(prev[pageId] || {}), start: val } }));
                          }}
                          showTimeSelect
                          timeFormat="hh:mm aa"
                          dateFormat={DATE_FMT}
                          timeIntervals={15}
                          className="border border-gray-300 rounded-md px-2 py-[2px] text-[11px] w-[105px]"
                        />
                        <span className="text-gray-500">→</span>
                        <DatePicker
                          selected={pageTimes[pageId]?.end || norm(endTime)}
                          onChange={(rawVal) => {
                            const pageStart = pageTimes[pageId]?.start || norm(startTime);
                            const oldEnd = pageTimes[pageId]?.end || norm(endTime);

                            const isDateOnly =
                              rawVal.getHours() === 0 && rawVal.getMinutes() === 0 && rawVal.getSeconds() === 0;

                            if (isDateOnly) {
                              const v = new Date(rawVal);
                              if (v > norm(endTime)) return showTimeError("Page end cannot exceed scheduling end");
                              if (v < pageStart) return showTimeError("Page end cannot be before page start");
                              console.log("🟩 PAGE END TIME CHANGE", {
  pageId,
  newEnd: val,
  oldStart: pageStart,
  oldEnd,
});

setPageTimes((prev) => ({ ...prev, [pageId]: { ...(prev[pageId] || {}), end: val } }));

                              setPageTimes((prev) => ({ ...prev, [pageId]: { ...(prev[pageId] || {}), end: v } }));
                              return;
                            }

                            // handle time-only correctly
                            const oldDate = oldEnd.toDateString();
                            const rawDate = rawVal.toDateString();

                            let val;
                            if (rawDate !== oldDate) {
                              val = new Date(rawVal);
                            } else {
                              val = new Date(oldEnd);
                              val.setHours(rawVal.getHours());
                              val.setMinutes(rawVal.getMinutes());
                              val.setSeconds(rawVal.getSeconds());
                            }

                            if (val.getTime() > norm(endTime).getTime()) {
                              return showTimeError("Page end cannot exceed scheduling end");
                            }
                            if (val.getTime() < pageStart.getTime()) {
                              return showTimeError("Page end cannot be before page start");
                            }

                            setPageTimes((prev) => ({ ...prev, [pageId]: { ...(prev[pageId] || {}), end: val } }));
                          }}
                          showTimeSelect
                          timeFormat="hh:mm aa"
                          dateFormat={DATE_FMT}
                          timeIntervals={15}
                          className="border border-gray-300 rounded-md px-2 py-[2px] text-[11px] w-[105px]"
                        />

                        <button
                          onClick={() =>
                            setEditingPages((prev) => ({
                              ...prev,
                              [pageId]: false,
                            }))
                          }
                          className="text-[11px] text-green-600 hover:text-green-800 ml-1"
                        >
                          Done
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Rows (inside expanded Page) */}
                  {isExpanded && pageRowsMap[pageId] && (
                    <Droppable droppableId={`rows-${pageId}`} type="ROW">
                      {(provided) => (
                        <div
                          className="mt-3 border-l-4 border-green-200 pl-3 space-y-3 ml-1"
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {pageRowsMap[pageId].map((row, index) => {
                            const rowId = row.id;
                            const rowTime = (rowTimes[pageId] && rowTimes[pageId][rowId]) || {};
                            const pageTime = pageTimes[pageId] || { start: norm(startTime), end: norm(endTime) };
                            const rowOverridden = isOverridden(rowTime, pageTime);
                            const tileOverridden = hasRowTileOverride(pageId, row);
                            const rowExpanded = expandedRows[rowId];

                            return (
                              <Draggable
                                key={`row-${pageId}-${rowId}`}
                                draggableId={`row-${pageId}-${rowId}`}
                                index={index}
                              >
                                {(providedRow) => (
                                  <div
                                    ref={providedRow.innerRef}
                                    {...providedRow.draggableProps}
                                    {...providedRow.dragHandleProps}
                                    className={`rounded-md p-2 space-y-2 ml-2 shadow-sm transition-all
                                      ${
                                        rowOverridden
                                          ? "border-2 border-yellow-500 bg-yellow-50"
                                          : tileOverridden
                                          ? "border-2 border-blue-500 bg-blue-50"
                                          : "border-l-4 border-blue-300 bg-blue-50/30"
                                      }`}
                                  >
                                    {/* Row Header + Editable Time */}
                                    <div className="flex flex-col">
                                      <div className="flex items-center justify-between font-semibold text-sm">
                                        {/* Left: Row title + Clock */}
                                        <div className="flex items-center">
                                          <span>{row.row_header}</span>

                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setEditingRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
                                            }}
                                            className="ml-[10px] text-blue-600 hover:text-blue-800"
                                          >
                                            <AiOutlineClockCircle size={14} />
                                          </button>
                                        </div>

                                        {/* Right: Expand Arrow */}
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleRowExpand(pageId, row);
                                          }}
                                          className="text-gray-600 bg-green-200 rounded-full hover:text-black mr-[2px]"
                                        >
                                          {rowExpanded ? <IoChevronDown /> : <IoChevronForward />}
                                        </button>
                                      </div>

                                      {/* Row pickers visible only in edit mode */}
                                      {editingRows[rowId] && (
                                        <div className="flex flex-wrap items-center gap-1 mt-1 text-xs">
                                          <DatePicker
                                            selected={rowTime.start || pageTime.start}
                                            onChange={(rawVal) => {
                                              const pageStart = pageTime.start || norm(startTime);
                                              const pageEnd = pageTime.end || norm(endTime);
                                              const currentEnd = rowTime.end || pageEnd;

                                              // date-only -> set directly
                                              const isDateOnly =
                                                rawVal.getHours() === 0 &&
                                                rawVal.getMinutes() === 0 &&
                                                rawVal.getSeconds() === 0;
                                              if (isDateOnly) {
                                                const v = new Date(rawVal);
                                                if (v < pageStart) return showTimeError("Row start cannot be earlier than page start");
                                                if (v > currentEnd) return showTimeError("Row start cannot exceed row end");
                                                console.log("🟧 ROW START TIME CHANGE", {
  pageId,
  rowId,
  newStart: v,
  oldStart: rowTime.start,
  oldEnd: currentEnd,
});

                                                setRowTimes((prev) => ({
                                                  ...prev,
                                                  [pageId]: { ...(prev[pageId] || {}), [rowId]: { ...(prev[pageId]?.[rowId] || {}), start: v } },
                                                }));
                                                return;
                                              }

                                              // time-only fix
                                              const oldDate = (rowTime.start || pageStart).toDateString();
                                              const rawDate = rawVal.toDateString();
                                              let val;
                                              if (rawDate !== oldDate) {
                                                val = new Date(rawVal);
                                              } else {
                                                val = new Date(rowTime.start || pageStart);
                                                val.setHours(rawVal.getHours());
                                                val.setMinutes(rawVal.getMinutes());
                                                val.setSeconds(rawVal.getSeconds());
                                              }

                                              if (val < pageStart) return showTimeError("Row start cannot be earlier than page start");
                                              if (val > currentEnd) return showTimeError("Row start cannot exceed row end");

                                              setRowTimes((prev) => ({
                                                ...prev,
                                                [pageId]: { ...(prev[pageId] || {}), [rowId]: { ...(prev[pageId]?.[rowId] || {}), start: val } },
                                              }));
                                            }}
                                            showTimeSelect
                                            timeFormat="hh:mm aa"
                                            dateFormat={DATE_FMT}
                                            timeIntervals={15}
                                            className="border border-gray-300 rounded-md px-2 py-[2px] text-[11px] w-[105px]"
                                          />
                                          <span className="text-gray-500">→</span>
                                          <DatePicker
                                            selected={rowTime.end || pageTime.end}
                                            onChange={(rawVal) => {
                                              const pageStart = pageTime.start || norm(startTime);
                                              const pageEnd = pageTime.end || norm(endTime);
                                              const rowStart = rowTime.start || pageStart;
                                              const oldEnd = rowTime.end || pageEnd;

                                              const isDateOnly =
                                                rawVal.getHours() === 0 &&
                                                rawVal.getMinutes() === 0 &&
                                                rawVal.getSeconds() === 0;
                                              if (isDateOnly) {
                                                const v = new Date(rawVal);
                                                if (v > pageEnd) return showTimeError("Row end cannot exceed page end");
                                                if (v < rowStart) return showTimeError("Row end cannot be before row start");
                                                console.log("🟧 ROW END TIME CHANGE", {
  pageId,
  rowId,
  newEnd: v,
  oldStart: rowStart,
  oldEnd,
});

                                                setRowTimes((prev) => ({
                                                  ...prev,
                                                  [pageId]: { ...(prev[pageId] || {}), [rowId]: { ...(prev[pageId]?.[rowId] || {}), end: v } },
                                                }));
                                                return;
                                              }

                                              const rawDate = rawVal.toDateString();
                                              const oldDate = oldEnd.toDateString();

                                              let val;
                                              if (rawDate !== oldDate) {
                                                val = new Date(rawVal);
                                              } else {
                                                val = new Date(oldEnd);
                                                val.setHours(rawVal.getHours());
                                                val.setMinutes(rawVal.getMinutes());
                                                val.setSeconds(rawVal.getSeconds());
                                              }

                                              if (val > pageEnd) return showTimeError("Row end cannot exceed page end");
                                              if (val < rowStart) return showTimeError("Row end cannot be before row start");

                                              setRowTimes((prev) => ({
                                                ...prev,
                                                [pageId]: { ...(prev[pageId] || {}), [rowId]: { ...(prev[pageId]?.[rowId] || {}), end: val } },
                                              }));
                                            }}
                                            showTimeSelect
                                            timeFormat="hh:mm aa"
                                            dateFormat={DATE_FMT}
                                            timeIntervals={15}
                                            className="border border-gray-300 rounded-md px-2 py-[2px] text-[11px] w-[105px]"
                                          />

                                          <button
                                            onClick={() =>
                                              setEditingRows((p) => ({
                                                ...p,
                                                [rowId]: false,
                                              }))
                                            }
                                            className="text-[11px] text-green-600 hover:text-green-800 ml-1"
                                          >
                                            Done
                                          </button>
                                        </div>
                                      )}
                                    </div>

                                    {/* Tiles */}
                                    {rowExpanded && rowTilesMap[pageId]?.[rowId] && (
                                      <Droppable droppableId={`${pageId}__${rowId}`}>
                                        {(provided) => (
                                          <div
                                            className="max-h-52 overflow-y-auto border-l-4 border-purple-300 pl-3 ml-2 bg-purple-50/20 rounded-md space-y-1"
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                          >
                                            {rowTilesMap[pageId]?.[rowId]?.map((tile, idx) => {
                                              const tid = tile.tid;
                                              const tileTime = (tileTimes[pageId]?.[rowId] || {})[tid] || {};
                                              const tileOver = isOverridden(tileTime, rowTime.start ? rowTime : pageTime);

                                              return (
                                                <Draggable key={String(tid)} draggableId={String(tid)} index={idx}>
                                                  {(providedTile) => (
                                                    <div
                                                      ref={providedTile.innerRef}
                                                      {...providedTile.draggableProps}
                                                      {...providedTile.dragHandleProps}
                                                      className={`p-2 border rounded-md
                                                      ${isOverridden(tileTime, rowTime.start ? rowTime : pageTime) ? "border-yellow-500 bg-yellow-50" : "bg-white"}`}
                                                    >
                                                      <div className="flex justify-between items-center">
                                                        <span className="truncate max-w-[60%] font-medium">{tile.title || "(No Title)"}</span>

                                                        <div className="flex items-center gap-2">
                                                          {!editingTiles[tid] ? (
                                                            <button
                                                              onClick={() =>
                                                                setEditingTiles((prev) => ({
                                                                  ...prev,
                                                                  [tid]: true,
                                                                }))
                                                              }
                                                              className="text-[10px] text-blue-600 hover:text-blue-800 font-medium"
                                                            >
                                                              <AiOutlineClockCircle size={14} />
                                                            </button>
                                                          ) : (
                                                            <button
                                                              onClick={() =>
                                                                setEditingTiles((prev) => ({
                                                                  ...prev,
                                                                  [tid]: false,
                                                                }))
                                                              }
                                                              className="text-[10px] text-green-600 hover:text-green-800 font-medium"
                                                            >
                                                              Done
                                                            </button>
                                                          )}
                                                        </div>
                                                      </div>

                                                      {editingTiles[tid] && (
                                                        <div className="flex items-center gap-1 mt-1 text-[10px]">
                                                          <DatePicker
                                                            selected={tileTime.start || rowTime.start || pageTime.start || norm(startTime)}
                                                            onChange={(rawVal) => {
                                                              const pageStart = pageTime.start || norm(startTime);
                                                              const pageEnd = pageTime.end || norm(endTime);
                                                              const rowStart = rowTime.start || pageStart;
                                                              const rowEnd = rowTime.end || pageEnd;
                                                              const currentEnd = tileTime.end || rowEnd;

                                                              const isDateOnly =
                                                                rawVal.getHours() === 0 &&
                                                                rawVal.getMinutes() === 0 &&
                                                                rawVal.getSeconds() === 0;
                                                              if (isDateOnly) {
                                                                const v = new Date(rawVal);
                                                                if (v < rowStart) return showTimeError("Tile start cannot be earlier than row start");
                                                                if (v > currentEnd) return showTimeError("Tile start cannot exceed tile end");
                                                                console.log("🟥 TILE START TIME CHANGE", {
  pageId,
  rowId,
  tileId: tid,
  newStart: v,
  oldStart: tileTime.start,
  oldEnd: currentEnd,
});

                                                                setTileTimes((prev) => ({
                                                                  ...prev,
                                                                  [pageId]: {
                                                                    ...(prev[pageId] || {}),
                                                                    [rowId]: { ...(prev[pageId]?.[rowId] || {}), [tid]: { ...(prev[pageId]?.[rowId]?.[tid] || {}), start: v } },
                                                                  },
                                                                }));
                                                                return;
                                                              }

                                                              const oldDate = (tileTime.start || rowStart).toDateString();
                                                              const rawDate = rawVal.toDateString();

                                                              let val;
                                                              if (rawDate !== oldDate) {
                                                                val = new Date(rawVal);
                                                              } else {
                                                                val = new Date(tileTime.start || rowStart);
                                                                val.setHours(rawVal.getHours());
                                                                val.setMinutes(rawVal.getMinutes());
                                                                val.setSeconds(rawVal.getSeconds());
                                                              }

                                                              if (val < rowStart) return showTimeError("Tile start cannot be earlier than row start");
                                                              if (val > currentEnd) return showTimeError("Tile start cannot exceed tile end");

                                                              setTileTimes((prev) => ({
                                                                ...prev,
                                                                [pageId]: {
                                                                  ...(prev[pageId] || {}),
                                                                  [rowId]: { ...(prev[pageId]?.[rowId] || {}), [tid]: { ...(prev[pageId]?.[rowId]?.[tid] || {}), start: val } },
                                                                },
                                                              }));
                                                            }}
                                                            showTimeSelect
                                                            timeFormat="hh:mm aa"
                                                            dateFormat={DATE_FMT}
                                                            className="border border-gray-300 rounded-md px-2 py-[2px] text-[10px] w-[100px]"
                                                          />
                                                          <span className="text-gray-500">→</span>
                                                          <DatePicker
                                                            selected={tileTime.end || rowTime.end || pageTime.end || norm(endTime)}
                                                            onChange={(rawVal) => {
                                                              const pageStart = pageTime.start || norm(startTime);
                                                              const pageEnd = pageTime.end || norm(endTime);
                                                              const rowStart = rowTime.start || pageStart;
                                                              const rowEnd = rowTime.end || pageEnd;
                                                              const tileStart = tileTime.start || rowStart;

                                                              const isDateOnly =
                                                                rawVal.getHours() === 0 &&
                                                                rawVal.getMinutes() === 0 &&
                                                                rawVal.getSeconds() === 0;
                                                              if (isDateOnly) {
                                                                const v = new Date(rawVal);
                                                                if (v > rowEnd) return showTimeError("Tile end cannot exceed row end");
                                                                if (v < tileStart) return showTimeError("Tile end cannot be before tile start");
                                                                console.log("🟥 TILE END TIME CHANGE", {
  pageId,
  rowId,
  tileId: tid,
  newEnd: v,
  oldStart: tileStart,
  oldEnd: oldEnd,
});

                                                                setTileTimes((prev) => ({
                                                                  ...prev,
                                                                  [pageId]: {
                                                                    ...(prev[pageId] || {}),
                                                                    [rowId]: { ...(prev[pageId]?.[rowId] || {}), [tid]: { ...(prev[pageId]?.[rowId]?.[tid] || {}), end: v } },
                                                                  },
                                                                }));
                                                                return;
                                                              }

                                                              const oldEnd = tileTime.end || rowEnd;
                                                              const rawDate = rawVal.toDateString();
                                                              const oldDate = oldEnd.toDateString();

                                                              let val;
                                                              if (rawDate !== oldDate) {
                                                                val = new Date(rawVal);
                                                              } else {
                                                                val = new Date(oldEnd);
                                                                val.setHours(rawVal.getHours());
                                                                val.setMinutes(rawVal.getMinutes());
                                                                val.setSeconds(rawVal.getSeconds());
                                                              }

                                                              if (val > rowEnd) return showTimeError("Tile end cannot exceed row end");
                                                              if (val < tileStart) return showTimeError("Tile end cannot be before tile start");

                                                              setTileTimes((prev) => ({
                                                                ...prev,
                                                                [pageId]: {
                                                                  ...(prev[pageId] || {}),
                                                                  [rowId]: { ...(prev[pageId]?.[rowId] || {}), [tid]: { ...(prev[pageId]?.[rowId]?.[tid] || {}), end: val } },
                                                                },
                                                              }));
                                                            }}
                                                            showTimeSelect
                                                            timeFormat="hh:mm aa"
                                                            dateFormat={DATE_FMT}
                                                            className="border border-gray-300 rounded-md px-2 py-[2px] text-[10px] w-[100px]"
                                                          />
                                                        </div>
                                                      )}
                                                    </div>
                                                  )}
                                                </Draggable>
                                              );
                                            })}
                                            {provided.placeholder}
                                          </div>
                                        )}
                                      </Droppable>
                                    )}
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <Pagination currentPage={page} totalPages={totalPages} onPageChange={(p) => setPage(p)} />

        <Button
          variant="success"
          onClick={handleSaveClick}
          disabled={isSaving}
          className={`px-5 py-1 my-2 text-sm flex items-center gap-3 mx-auto ${isSaving ? "opacity-70 cursor-not-allowed" : ""}`}
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              Saving...
            </>
          ) : (
            "Save page"
          )}
        </Button>
      </div>
    </DragDropContext>
  );
}
